import { Flex, Text, Box } from '@chakra-ui/react';
import type { Review } from '@/models/review/review';
import type { Product } from '@/models/product/product';
import LucideIcon from '@/components/ui/LucideIcon';
import { formatRelativeDate } from '@/utils/domain';
import Image from 'next/image';
import { getTestimonialDisplayImage } from '../utils/imageUtils';
import { SafeLink } from '@/components/ui/SafeLink';

interface TestimonialCardProps {
  review: Review;
  product?: Product;
}

const TestimonialCard = ({ review, product }: TestimonialCardProps) => {
  const displayImage = getTestimonialDisplayImage(review, product);

  return (
    <Box width="100%" borderRadius="8px" backgroundColor="common.100" height="100%">
      <Flex flexDirection="column" width="100%" gap={{ base: 3, tablet: 4 }} height="100%">
        <Box
          width="100%"
          height={{ base: '200px', tablet: '248px' }}
          borderRadius="8px"
          overflow="hidden"
          position="relative"
          backgroundColor="neutral.5"
        >
          {displayImage ? (
            <Image src={displayImage} alt={review.title || 'Product'} style={{ objectFit: 'cover' }} fill />
          ) : (
            <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
              <LucideIcon name="Image" boxSize={8} color="neutral.30" />
            </Flex>
          )}
        </Box>

        <Flex justifyContent="space-between" alignItems="center">
          <Flex gap={2} alignItems="center">
            <Flex alignItems="center" gap={1}>
              <LucideIcon name="CircleCheck" width={13} height={13} color="neutral.60" />
              <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
                Verified purchase
              </Text>
            </Flex>
          </Flex>
          <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
            {formatRelativeDate(review.modifiedAt)}
          </Text>
        </Flex>

        <Flex gap={3} alignItems="flex-start">
          <Flex
            width="60px"
            height="60px"
            backgroundColor="neutral.5"
            padding={3}
            borderRadius="8px"
            overflow="hidden"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            {displayImage ? (
              <Image src={displayImage} alt={review.name} style={{ objectFit: 'cover' }} fill />
            ) : (
              <LucideIcon name="Image" boxSize={{ base: 4, tablet: 5 }} color="neutral.30" />
            )}
          </Flex>

          <Text color="neutral.90" textStyle="body3" fontWeight="medium" flex={1} alignSelf="center">
            {product?.productLink ? (
              <SafeLink
                href={`${product.productLink}#judgeme_product_reviews`}
                _hover={{ textDecoration: 'underline' }}
              >
                {review.title || `Review #${review.reviewId}`}
              </SafeLink>
            ) : (
              review.title || `Review #${review.reviewId}`
            )}
          </Text>
        </Flex>

        <Text color="neutral.60" textStyle="body3" fontWeight="regular" overflow="hidden" lineClamp={6} minHeight={200}>
          {review.body}
        </Text>

        <Flex justifyContent="space-between" alignItems="center" width="100%" mt="auto">
          <Flex gap={3} alignItems="center">
            <Flex
              width="12px"
              height="12px"
              borderRadius="50%"
              backgroundColor="neutral.10"
              justifyContent="center"
              alignItems="center"
              overflow="hidden"
              position="relative"
            >
              <LucideIcon name="User" boxSize={2} color="neutral.30" />
            </Flex>
            <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
              {review.name}
            </Text>
            <Flex gap={1}>
              {[...Array(5)].map((_, i) => (
                <LucideIcon
                  name="Star"
                  key={i}
                  boxSize={4}
                  fill={i < review.rating ? '#FF9200' : 'none'}
                  stroke={i < review.rating ? '#FF9200' : 'neutral.30'}
                />
              ))}
            </Flex>
          </Flex>
        </Flex>

        <Box height="1px" width="100%" backgroundColor="neutral.5" />

        <Flex
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', tablet: 'center' }}
          width="100%"
          flexDirection={{ base: 'row' }}
          gap={{ base: 2, tablet: 0 }}
        >
          <Flex gap={2} alignItems="center">
            <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
              Skin type:
            </Text>
            <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
              {review.skinType && review.skinType.length > 0 ? review.skinType.join(', ') : 'Not specified'}
            </Text>
          </Flex>

          <Flex gap={2} alignItems="center">
            <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
              Skin concern:
            </Text>
            <Text color="neutral.60" textStyle="caption1" fontWeight="regular">
              {review.skinConcern && review.skinConcern.length > 0 ? review.skinConcern.join(', ') : 'Not specified'}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default TestimonialCard;
